import React, { type ComponentType, type FC, type ReactNode } from "react"

import { captureException } from "./sentry-state"

export interface ErrorBoundaryProps {
  fallback: ComponentType<{
    error: Error
    componentStack: string
    resetError: () => void
  }>
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  componentStack: string
}

class ErrorBoundaryClass extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, componentStack: "" }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: { componentStack: string }) {
    captureException(error, {
      level: "fatal",
      tags: { error_boundary: "react" },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    })

    this.setState({
      error,
      componentStack: errorInfo.componentStack,
    })
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, componentStack: "" })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback
      return React.createElement(FallbackComponent, {
        error: this.state.error,
        componentStack: this.state.componentStack,
        resetError: this.resetError,
      })
    }

    return this.props.children
  }
}

export const ErrorBoundary: FC<ErrorBoundaryProps> = (props) => {
  return React.createElement(ErrorBoundaryClass, props)
}
